// Learning-plan generator + persistence for the Homeschool Command Centre.
// v2 SCHEMA: multi-child support.
//
// Storage key: `we_learning_plan_v2`
// Shape:
//   {
//     children:   { [id]: { id, name, age, curriculum, subjects, dailyMinutes, focus, createdAt, token } },
//     plans:      { [id]: { weekNumber, generatedAt, days: [...] } },
//     completion: { [id]: { "Monday::activity1": true, ... } },
//     activeId:   "<id>" | null
//   }
//
// v1 (single-child) auto-migrates on first load — no data loss.

import { MODULE_LIBRARY, SUBJECT_LABEL } from "@/lib/curriculum";
import { SYLLABUS } from "@/lib/syllabus";
import { outcomesForSubject } from "@/lib/subjectAliases";
import { TOPIC_BY_OUTCOME } from "@/lib/topicContent";
// Cycle 62 · Task 4 · Full Weekly Plan bake-in. Pull the 83 Claude-
// authored Maths Academy lessons in statically so the 36-week schedule
// can point maths slots at specific curriculum-mapped lessons without a
// runtime fetch. When a child has a `year` set, MA lessons for that
// year REPLACE the standard maths outcomes; otherwise the plan falls
// back to the syllabus outcomes as before.
import MATHS_ACADEMY_DATA from "@/data/mathsAcademyLessons.json";
import { yearForChild } from "@/lib/schoolYear";

const STORAGE_KEY    = "we_learning_plan_v2";
const OLD_STORAGE_V1 = "we_learning_plan_v1";

// ────────────────────────────────────────────────────────────────────
//  CURRICULUM-DRIVEN 36-WEEK PLAN (Phase 2)
//  --------------------------------------------------------------------
//  Per the "FIX THE CORE LOGIC" spec, the schedule is now built FROM
//  syllabus outcomes (uk-ks2-geo-1, ib-pyp-sc-2, …), NOT from arbitrary
//  app activities. Every scheduled day-item references a real outcome;
//  activities are only attached as ways to TEACH each outcome.
//
//  Constants:
//    TOTAL_WEEKS      — one school year = 36 weeks (STEP 2)
//    LEARN_MINUTES    — default "Learn this" lesson minutes per outcome
//    QUIZ_MINUTES     — default follow-up quiz minutes per outcome
// ────────────────────────────────────────────────────────────────────
const TOTAL_WEEKS   = 36;
const LEARN_MINUTES = 15;
const QUIZ_MINUTES  = 5;
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// Every homeschool subject needs to map to a Knowledge Quest "world" so
// each outcome carries a working quiz link. Kept in sync with the tabs
// on /knowledge.
const KNOWLEDGE_BY_SUBJECT = {
  geography: "nature",
  maths: "maths",
  science: "science",
  english: "english",
  history: "history",
  nature: "nature",
  space: "space",
  languages: "languages",
  art: "art",
  ict: "coding",
  pe: "pe",
  re: "re",
  pshe: "pshe",
  creative: "art",
};

/**
 * All outcomes for a child, filtered to their chosen curriculum + age band
 * and grouped by the PARENT-facing subject id (matches SUBJECTS in
 * curriculum.js). Outcomes are returned in the syllabus's authored order
 * — that's already foundational → harder, so we don't re-sort (STEP 2).
 *
 * Pool-sizing rules (in priority order):
 *   1) Prefer the parent-chosen curriculum for that subject at the child's
 *      age.
 *   2) If that's a very short list (<5 outcomes) we SUPPLEMENT with the
 *      same subject from any curriculum — this is common for age 7 UK
 *      KS2 subjects where most outcomes' ageBand starts at 8-9.
 *   3) If we still find zero, widen to any curriculum outright.
 *
 * Coverage guarantee (STEP 4): every selected subject always ends up
 * with at least one outcome to schedule — never silently skipped.
 */
function outcomesForChild(profile) {
  const { age = 8, curriculum = "uk-ks2", subjects = [] } = profile || {};
  const bucket = {};
  // Feb 2026 · Bug #6 fix — Product decision: the 36-week plan MUST
  // scope to the child's one selected curriculum. Previous behaviour
  // silently supplemented with outcomes from other curricula whenever
  // the strict pool had < 5 items, which produced weeks blending
  // UK-KS1 / UK-KS2 / CAM-PRIM / UAE-MOE codes. That's now off:
  // strict-only, curriculum ONLY. When a subject has no outcomes for
  // the picked curriculum at the child's age, the subject renders an
  // empty state instead of pulling from foreign frameworks.
  for (const subj of subjects) {
    // Cycle 62 · Task 4 · If subject is maths AND we can resolve a
    // year for the child, use the Maths Academy lessons for that year
    // as the authoritative outcomes. This spreads the year's ~12-16
    // lessons across the 36 weeks (with revision cycles once every
    // lesson has been visited) so every scheduled maths slot points at
    // a specific MA lesson.
    if (subj === "maths") {
      const year = profile?.year ?? (profile ? yearForChild(profile) : null);
      const maOutcomes = mathsAcademyOutcomesForYear(year);
      if (maOutcomes.length) {
        bucket[subj] = maOutcomes;
        continue;
      }
    }
    const strict = outcomesForSubject(SYLLABUS, subj, age)
      .filter((o) => curriculum === "other" || o.curriculum === curriculum);
    // Dedupe by outcome code (rare but possible when the same code
    // is authored in two age-bands).
    const seen = new Set();
    bucket[subj] = strict.filter((o) => {
      if (seen.has(o.code)) return false;
      seen.add(o.code);
      return true;
    });
  }
  return bucket;
}

/**
 * Cycle 62 · Task 4 — build pseudo-outcomes from the Maths Academy
 * lesson set for a given year (1..10). Each pseudo-outcome carries an
 * `mathsAcademy` marker + the lesson id so `enrichOutcome` can route
 * the day-card to `/academy/maths/library?id=<lessonId>` instead of the
 * standard /learn-topics deep link.
 */
function mathsAcademyOutcomesForYear(year) {
  if (!Number.isFinite(year)) return [];
  const all = Array.isArray(MATHS_ACADEMY_DATA?.lessons) ? MATHS_ACADEMY_DATA.lessons : [];
  const forYear = all.filter((l) => l.uk_year === year);
  if (forYear.length === 0) return [];
  // Order lessons deterministically by strand + title so the schedule
  // walks the year in a sensible sequence (Place Value → operations →
  // fractions → measurement → geometry → stats).
  const STRAND_ORDER = [
    "Place Value",
    "Addition & Subtraction",
    "Multiplication & Division",
    "Fractions",
    "Decimals",
    "Percentages",
    "Ratio & Proportion",
    "Algebra",
    "Measurement",
    "Geometry",
    "Statistics",
  ];
  const ordered = [...forYear].sort((a, b) => {
    const ai = STRAND_ORDER.indexOf(a.strand);
    const bi = STRAND_ORDER.indexOf(b.strand);
    if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return String(a.title).localeCompare(String(b.title));
  });
  return ordered.map((l) => ({
    code: `maths-academy:${l.id}`,
    outcome: l.title,
    curriculum: "maths-academy",
    ageBand: `Y${l.uk_year}`,
    // Carrier fields consumed by enrichOutcome + day-card renderers.
    mathsAcademy: true,
    lessonId: l.id,
    strand: l.strand,
    emoji: "🧮",
  }));
}

/**
 * Distribute a subject's outcomes across TOTAL_WEEKS in the curriculum's
 * intended order. Design goal: consecutive weeks tap DIFFERENT outcomes
 * first so parents see visible progression when they click "Next week"
 * or jump to another week. Only once every outcome has been visited do
 * we begin the revision cycle.
 *
 *   • Weeks 1..N     → outcome[0], outcome[1], … outcome[N-1] (fresh)
 *   • Weeks N+1..36  → outcome[w % N] with revision:true
 *
 * This replaces the previous stride-placement approach which clumped
 * many revision weeks together and made weeks 1↔2↔10 look identical.
 */
function scheduleSubjectAcrossYear(subjectId, outcomes) {
  const slots = new Array(TOTAL_WEEKS).fill(null);
  if (!outcomes.length) return slots;
  for (let w = 0; w < TOTAL_WEEKS; w++) {
    const idx = w % outcomes.length;
    slots[w] = {
      outcome: outcomes[idx],
      revision: w >= outcomes.length, // fresh once, then cycled
    };
  }
  return slots;
}

/**
 * Enrich an outcome record with everything a day-card needs: the linked
 * teaching topic (if authored in topicContent.js), the deep-link into
 * /learn-topics, the matching Knowledge Quest quiz, and an OPTIONAL
 * practice activity from MODULE_LIBRARY (games/globe/colouring — only if
 * one exists for the same subject at the child's age).
 */
function enrichOutcome(o, subjectId, age, revision) {
  // Cycle 62 · Maths Academy branch — MA pseudo-outcomes route to the
  // library page instead of /learn-topics so day-cards land directly
  // in the interactive lesson (Learn → Try → Practice → Mastery), and
  // hasLesson stays true so the "next lesson" surfaces treat MA slots
  // as first-class content.
  if (o?.mathsAcademy) {
    const to = `/academy/maths/library?id=${encodeURIComponent(o.lessonId)}`;
    return {
      id: `${o.code}${revision ? ":rev" : ""}`,
      kind: "outcome",
      revision,
      code: o.code,
      subject: subjectId,
      subjectLabel: SUBJECT_LABEL[subjectId] || subjectId,
      outcome: o.outcome,
      outcomeShort: shortenOutcome(o.outcome),
      curriculum: o.curriculum,
      ageBand: o.ageBand,
      topicId: o.lessonId,
      topicTitle: o.outcome,
      topicEmoji: o.emoji || "🧮",
      hasLesson: true,
      mathsAcademy: true,
      strand: o.strand,
      learnMinutes: LEARN_MINUTES,
      quizMinutes: QUIZ_MINUTES,
      minutes: LEARN_MINUTES + QUIZ_MINUTES,
      learnTo: to,
      quizSection: null,
      quizTo: null,
      route: to,
      standards: ["maths-academy"],
      practice: null,
      title: `${o.outcome} · ${SUBJECT_LABEL[subjectId] || subjectId}`,
    };
  }
  const topics = TOPIC_BY_OUTCOME[o.code];
  const topic = topics && topics.length ? topics[0] : null;
  const quizSection = KNOWLEDGE_BY_SUBJECT[subjectId] || null;
  const practice = MODULE_LIBRARY.find(
    (m) => m.subject === subjectId && age >= m.ages[0] && age <= m.ages[1],
  );
  return {
    id: `${o.code}${revision ? ":rev" : ""}`,
    kind: "outcome",
    revision,
    code: o.code,
    subject: subjectId,
    subjectLabel: SUBJECT_LABEL[subjectId] || subjectId,
    outcome: o.outcome,
    outcomeShort: shortenOutcome(o.outcome),
    curriculum: o.curriculum,
    ageBand: o.ageBand,
    topicId: topic?.id || null,
    topicTitle: topic?.title || null,
    topicEmoji: topic?.emoji || null,
    hasLesson: !!topic,
    // Timing (spec: "Learn (15 min) · then Quiz (5 min)")
    learnMinutes: LEARN_MINUTES,
    quizMinutes: QUIZ_MINUTES,
    minutes: LEARN_MINUTES + QUIZ_MINUTES,
    // Primary CTA — read the outcome's teaching card.
    learnTo: `/learn-topics?s=${subjectId}#${o.code}`,
    // Follow-up assessment.
    quizSection,
    quizTo: quizSection ? `/knowledge/${quizSection}` : null,
    // Route used by legacy toggle handlers and Term Report (falls back to
    // the /learn-topics deep-link for backwards compat).
    route: `/learn-topics?s=${subjectId}#${o.code}`,
    // Standards tag list kept for TermReport aggregation.
    standards: [o.curriculum],
    // Optional practice attachment (labelled clearly, never a substitute).
    practice: practice ? {
      id: practice.id,
      title: practice.title,
      to: practice.route,
      emoji: practice.emoji,
    } : null,
    // Pretty title for the day-card. Renderers can override.
    title: `${topic?.title || shortenOutcome(o.outcome)} · ${SUBJECT_LABEL[subjectId] || subjectId}`,
  };
}

/**
 * A syllabus outcome like "Multiplication tables: recall all facts up to
 * 12 × 12." is fine for parents but a mouthful for the day-tile header.
 * Take the first clause before ":" if present, else the first ~40 chars.
 */
function shortenOutcome(text) {
  if (!text) return "";
  const colon = text.indexOf(":");
  if (colon > 0 && colon < 40) return text.slice(0, colon);
  if (text.length <= 40) return text;
  return `${text.slice(0, 40).trimEnd()}…`;
}

/**
 * Build the full 36-week schedule for a child. Pure and deterministic
 * (no PRNG), because the outcome order comes straight from the syllabus.
 * Returns:
 *   {
 *     profile, totalWeeks: 36,
 *     weeks: [{ weekNumber, days: [{ day, activities }], subjects, headerTopics }],
 *     coverage: { totalOutcomes, uniqueScheduled, byPercent, bySubject }
 *   }
 */
export function buildYearPlan(profile) {
  const { subjects = [], age = 8 } = profile || {};
  const outcomesBySubject = outcomesForChild(profile);

  // Schedule slot maps (subject → array of TOTAL_WEEKS slots).
  const slotMap = {};
  const activeSubjects = [];
  for (const s of subjects) {
    const outs = outcomesBySubject[s] || [];
    slotMap[s] = scheduleSubjectAcrossYear(s, outs);
    if (outs.length) activeSubjects.push(s);
  }
  // Any subject the parent picked but that returned zero outcomes at all
  // is still tracked in `activeSubjects` for coverage — we surface a
  // "content coming soon" tile so the schedule never silently drops it.
  for (const s of subjects) {
    if (!activeSubjects.includes(s)) activeSubjects.push(s);
  }

  // Weeks: for each week, gather each subject's slot and lay them out
  // Mon-Fri in a stable rotation so no single day is one-subject-only.
  const weeks = [];
  for (let w = 0; w < TOTAL_WEEKS; w++) {
    const weekItems = [];
    const headerTopics = [];
    for (const s of activeSubjects) {
      const slot = slotMap[s]?.[w] || null;
      if (!slot) {
        // Subject had zero outcomes → surface a "coming soon" tile once
        // per week rather than dropping the subject entirely.
        weekItems.push({
          id: `coming-soon:${s}:${w}`,
          kind: "placeholder",
          subject: s,
          subjectLabel: SUBJECT_LABEL[s] || s,
          title: `${SUBJECT_LABEL[s] || s} — rest day`,
          outcome: "",
          outcomeShort: "Free choice",
          minutes: 0,
          learnMinutes: 0,
          quizMinutes: 0,
          revision: false,
          hasLesson: false,
          standards: [],
          route: "/learn-topics",
          learnTo: `/learn-topics?s=${s}`,
        });
        headerTopics.push(`${SUBJECT_LABEL[s] || s}: (free choice)`);
        continue;
      }
      const item = enrichOutcome(slot.outcome, s, age, slot.revision);
      weekItems.push(item);
      const shortLbl = item.topicTitle || item.outcomeShort || "Learning";
      headerTopics.push(`${SUBJECT_LABEL[s] || s}: ${shortLbl}`);
    }
    // Column-first Mon-Fri layout keeps subjects mixed across days.
    const perDay = Math.max(1, Math.ceil(weekItems.length / DAYS.length));
    const days = DAYS.map((day, di) => {
      const dayItems = [];
      for (let k = 0; k < perDay; k++) {
        const idx = di + k * DAYS.length;
        if (idx < weekItems.length) dayItems.push(weekItems[idx]);
      }
      return { day, activities: dayItems };
    });

    weeks.push({
      weekNumber: w + 1,
      days,
      subjects: activeSubjects,
      headerTopics,
      itemCount: weekItems.length,
    });
  }

  // Coverage summary (STEP 4 validation surface).
  const coverage = computeCoverage(outcomesBySubject, slotMap, activeSubjects);

  return {
    profile,
    totalWeeks: TOTAL_WEEKS,
    activeSubjects,
    weeks,
    coverage,
    generatedAt: new Date().toISOString(),
  };
}

/** Coverage report — % of curriculum outcomes touched over 36 weeks. */
function computeCoverage(outcomesBySubject, slotMap, subjects) {
  const bySubject = {};
  let totalOutcomes = 0;
  let uniqueScheduled = 0;
  for (const s of subjects) {
    const outs = outcomesBySubject[s] || [];
    const slots = slotMap[s] || [];
    const scheduled = new Set(
      slots.filter(Boolean).map((sl) => sl.outcome.code),
    );
    bySubject[s] = {
      total: outs.length,
      scheduled: scheduled.size,
      percent: outs.length ? Math.round((scheduled.size / outs.length) * 100) : 0,
    };
    totalOutcomes    += outs.length;
    uniqueScheduled  += scheduled.size;
  }
  const byPercent = totalOutcomes
    ? Math.round((uniqueScheduled / totalOutcomes) * 100)
    : 0;
  return { totalOutcomes, uniqueScheduled, byPercent, bySubject };
}

/** Return just one week (thin wrapper around buildYearPlan). */
export function weekFromYearPlan(profile, weekNumber = 1) {
  const year = buildYearPlan(profile);
  const idx = Math.max(1, Math.min(TOTAL_WEEKS, weekNumber)) - 1;
  return {
    ...year.weeks[idx],
    totalWeeks: TOTAL_WEEKS,
    coverage: year.coverage,
    profile: year.profile,
    generatedAt: year.generatedAt,
  };
}

/** Short URL-safe ID (12 chars, ~62^12 space — plenty for local use). */
function makeId() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  const rand = (typeof crypto !== "undefined" && crypto.getRandomValues)
    ? Array.from(crypto.getRandomValues(new Uint32Array(12))).map((n) => n % chars.length)
    : Array.from({ length: 12 }, () => Math.floor(Math.random() * chars.length));
  for (const n of rand) out += chars[n];
  return out;
}

/** Small stable string hash so week seeds are deterministic. */
function hash(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/** Mulberry32 PRNG seeded from a hash — pure and deterministic. */
function rng(seed) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Curriculum-driven weekly plan (Phase 2 rewrite).
 *
 * Prior behaviour built each day from arbitrary MODULE_LIBRARY activities
 * labelled with a subject — parents kept seeing "Spin the 3D Globe · 10
 * min" and asking "but what is my child *learning*?". The new generator
 * takes the child's syllabus outcomes for the year, distributes them
 * across 36 weeks in curriculum order, and returns one week's slice of
 * that plan. Every day-item is an OUTCOME with Learn + Quiz attached.
 *
 * @param {Object} profile   { name, age, curriculum, subjects, dailyMinutes }
 * @param {number} weekNumber 1-based; clamps to [1, 36]
 */
export function generateWeeklyPlan(profile, weekNumber = 1) {
  const week = weekFromYearPlan(profile, weekNumber);
  return {
    weekNumber: week.weekNumber,
    totalWeeks: week.totalWeeks,
    days: week.days,
    subjects: week.subjects,
    headerTopics: week.headerTopics,
    coverage: week.coverage,
    generatedAt: week.generatedAt,
  };
}

/**
 * Curriculum-driven schedule generator — pulls actual syllabus outcomes
 * (not just app activities) so the plan reflects what a child SHOULD
 * learn this week/month/year according to UK KS1-3, IB PYP/MYP, Cambridge,
 * UAE MOE or Common Core. Each outcome is enriched with:
 *   • the linked teaching topic (if authored — from topicContent.js)
 *   • the closest in-app activity from MODULE_LIBRARY
 *   • completion / knowledge / assessment status (persisted per child).
 *
 * Returns {@link TermSchedule}: 12 weeks × 5 days × 2-3 outcomes/day, split
 * across the child's chosen subjects and rotated so no subject dominates.
 *
 * @param {Object} profile { name, age, curriculum, subjects, dailyMinutes }
 * @param {number} weeks   How many weeks to schedule (default: 12 = one term).
 * @param {number} termNum Which term (1/2/3) — seeds the shuffle so terms differ.
 */
export function generateTermSchedule(profile, weeks = 12, termNum = 1) {
  const {
    name = "Explorer",
    age = 8,
    curriculum = "uk-ks2",
    subjects = ["geography", "maths", "english", "science"],
  } = profile || {};

  // Collect every syllabus outcome that (a) matches the chosen curriculum
  // OR is a broad match when curriculum='other', (b) fits the child's age
  // band, and (c) belongs to one of the picked subjects.
  const pool = [];
  for (const subject of subjects) {
    const outs = outcomesForSubject(SYLLABUS, subject, age);
    for (const o of outs) {
      if (curriculum !== "other" && o.curriculum !== curriculum) continue;
      pool.push({ ...o, subjectId: subject });
    }
  }
  // Feb 2026 · Bug #6 fix — Product decision: annual term plan is
  // strictly scoped to the child's chosen curriculum. Widening
  // fallback removed so weeks can never blend UK-KS1 / UK-KS2 /
  // CAM-PRIM / UAE-MOE / CC-SS codes. Empty state is acceptable.

  const seed = hash(`${name}|${curriculum}|${subjects.join(",")}|T${termNum}`);
  const rand = rng(seed);

  // Enrich each outcome with teaching topic + a matching in-app activity
  // + the relevant knowledge-quiz section (Maths Missions, Word Wizards,
  // Art Studio, Body & Sport, World Beliefs, Feelings & Friends, etc.)
  // so parents get a real "test" link, not just a "learn" link.
  const KNOWLEDGE_BY_SUBJECT = {
    geography: "nature",   // Nature Trail — countries, rivers, mountains
    maths: "maths",
    science: "science",
    english: "english",
    history: "history",
    nature: "nature",
    space: "space",
    languages: "languages",
    art: "art",
    ict: "coding",
    pe: "pe",
    re: "re",
    pshe: "pshe",
    creative: "art",       // fallback for creative → Art Studio
  };
  const enrich = (o) => {
    const topics = TOPIC_BY_OUTCOME[o.code];
    const topic = topics && topics.length ? topics[0] : null;
    const activity = MODULE_LIBRARY.find(
      (m) => m.subject === o.subjectId && age >= m.ages[0] && age <= m.ages[1],
    );
    const quizSection = KNOWLEDGE_BY_SUBJECT[o.subjectId] || null;
    return {
      code: o.code,
      subject: o.subjectId,
      outcome: o.outcome,
      curriculum: o.curriculum,
      topicId: topic?.id || null,
      topicTitle: topic?.title || null,
      topicEmoji: topic?.emoji || "🎯",
      activityId: activity?.id || null,
      activityTitle: activity?.title || null,
      activityTo: activity?.to || null,
      // Deep-link so parents can jump straight to the teaching card.
      learnTo: `/learn-topics?s=${o.subjectId}#${o.code}`,
      // Matching knowledge-quiz hub — every subject now has a test.
      quizSection,
      quizTo: quizSection ? `/knowledge/${quizSection}` : null,
    };
  };

  // Bucket enriched outcomes BY SUBJECT so we can guarantee coverage of
  // every subject the family picked in every single week. Each subject
  // keeps its own deterministic shuffle + cycling cursor across weeks.
  const bySubject = {};
  for (const subject of subjects) {
    const items = pool.filter((o) => o.subjectId === subject).map(enrich);
    if (items.length) {
      // Deterministic per-subject shuffle so a subject with few outcomes
      // still cycles predictably across the term rather than repeating
      // the same objective every week.
      const subRand = rng(hash(`${seed}|${subject}`));
      const shuffled = [...items].sort(() => subRand() - 0.5);
      bySubject[subject] = shuffled;
    }
  }

  const activeSubjects = subjects.filter((s) => bySubject[s]?.length);
  const totalEnriched = activeSubjects.reduce(
    (n, s) => n + bySubject[s].length, 0);

  // Round-robin scheduler: every week emits ONE outcome per chosen subject
  // (cycling through that subject's shuffled list), then keeps going in the
  // same rotation to fill any remaining daily slots. That way 14 subjects
  // × 3-item days = 15 slots/week → every subject appears at least once.
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  const perDay = Math.max(2, Math.ceil(activeSubjects.length / days.length));
  const perWeek = perDay * days.length;

  // Per-subject cursor persists across weeks so each week picks the NEXT
  // objective from that subject, rather than always the first one.
  const subjCursor = Object.fromEntries(activeSubjects.map((s) => [s, 0]));
  let scheduled = 0;

  const buildWeek = (weekNumber) => {
    const items = [];
    let rotation = 0;
    // First pass: guarantee every subject once.
    for (const s of activeSubjects) {
      const bucket = bySubject[s];
      const item = bucket[subjCursor[s] % bucket.length];
      subjCursor[s] += 1;
      items.push(item);
      scheduled += 1;
    }
    // Fill remaining daily slots by continuing the round-robin. Skip
    // subjects that were already fully covered this week if extra room.
    while (items.length < perWeek && activeSubjects.length) {
      const s = activeSubjects[rotation % activeSubjects.length];
      const bucket = bySubject[s];
      items.push(bucket[subjCursor[s] % bucket.length]);
      subjCursor[s] += 1;
      scheduled += 1;
      rotation += 1;
    }

    // Distribute across Mon–Fri column-first so day 1 gets item[0],
    // item[5], item[10]…, day 2 gets item[1]… — keeps subjects mixed
    // across days rather than clumping the first 3 subjects on Monday.
    const dayPlans = days.map((day, di) => {
      const dayItems = [];
      for (let k = 0; k < perDay; k++) {
        const idx = di + k * days.length;
        if (idx < items.length) dayItems.push(items[idx]);
      }
      return { day, items: dayItems };
    });
    return { weekNumber, days: dayPlans };
  };

  const weekPlans = [];
  for (let w = 1; w <= weeks; w++) weekPlans.push(buildWeek(w));

  return {
    profile: { name, age, curriculum, subjects },
    termNum,
    weeks: weekPlans,
    totalOutcomes: totalEnriched,
    scheduledOutcomes: scheduled,
    perDay,
    activeSubjects,
    generatedAt: new Date().toISOString(),
  };
}

/** Convenience: month view = 4 weeks. */
export function generateMonthSchedule(profile, monthNum = 1) {
  return generateTermSchedule(profile, 4, monthNum);
}

/** Convenience: full year = 3 terms × 12 weeks = 36 weeks. */
export function generateYearSchedule(profile) {
  return [1, 2, 3].map((t) => generateTermSchedule(profile, 12, t));
}


// ── Storage helpers ────────────────────────────────────────────────
function readRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* fall through */ }
  return null;
}
function writeRaw(next) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* quota */ }
}

/** Auto-migrate v1 → v2 on first read. Idempotent. */
function migrateFromV1() {
  try {
    const raw = localStorage.getItem(OLD_STORAGE_V1);
    if (!raw) return null;
    const old = JSON.parse(raw);
    if (!old?.profile) return null;
    const id = makeId();
    const token = makeId();
    const child = {
      id, token,
      name: old.profile.childName || "Explorer",
      age:  old.profile.age || 7,
      curriculum: old.profile.curriculum || "uk-ks2",
      subjects:    old.profile.subjects || ["geography"],
      dailyMinutes: old.profile.dailyMinutes || 30,
      focus: old.profile.focus || "",
      createdAt: new Date().toISOString(),
    };
    return {
      children:  { [id]: child },
      plans:     { [id]: old.plan || generateWeeklyPlan(child, 1) },
      completion:{ [id]: old.completion || {} },
      activeId:  id,
    };
  } catch { return null; }
}

/** Load the multi-child state (auto-migrating v1 if needed).
 *
 *  Also upgrades in-place any plans persisted under the old
 *  activity-based schema (Phase 1 / early Phase 2) to the new
 *  curriculum-outcome-driven format. Detection is simple: outcome-driven
 *  plans always carry a `coverage` field and a `headerTopics` array on
 *  each week's slice. If those are missing, we rebuild from the child's
 *  profile — no user action required.
 */
export function loadState() {
  const cur = readRaw();
  if (cur && cur.children) {
    const upgraded = upgradeStalePlans(cur);
    if (upgraded !== cur) writeRaw(upgraded);
    return upgraded;
  }
  const migrated = migrateFromV1();
  if (migrated) {
    const upgraded = upgradeStalePlans(migrated);
    writeRaw(upgraded);
    return upgraded;
  }
  return { children: {}, plans: {}, completion: {}, activeId: null };
}

/** Return an updated `state` with every plan re-generated from the
 *  current outcome-driven pipeline if it looks pre-Phase-2. Otherwise
 *  returns the same object reference (cheap fast path). */
function upgradeStalePlans(state) {
  if (!state || !state.plans || !state.children) return state;
  let changed = false;
  const nextPlans = { ...state.plans };
  const nextCompletion = { ...(state.completion || {}) };
  for (const id of Object.keys(state.children)) {
    const plan = state.plans[id];
    const looksNew =
      plan && plan.coverage && Array.isArray(plan.headerTopics);
    if (looksNew) continue;
    // Rebuild the plan for the child's current week (or Week 1 if none).
    const week = plan?.weekNumber || 1;
    nextPlans[id] = generateWeeklyPlan(state.children[id], week);
    // Old completion keys reference activity ids that no longer exist,
    // so clear them out — safer than leaving orphaned check-marks.
    nextCompletion[id] = {};
    changed = true;
  }
  if (!changed) return state;
  return { ...state, plans: nextPlans, completion: nextCompletion };
}

/** LEGACY export kept for backwards-compat with earlier imports. */
export function loadPlanState() {
  const s = loadState();
  const id = s.activeId;
  if (!id || !s.children[id]) return { profile: null, plan: null, completion: {} };
  return {
    profile: s.children[id],
    plan:    s.plans[id] || null,
    completion: s.completion[id] || {},
  };
}

// ── Child roster mutations ────────────────────────────────────────
export function addChild(profile) {
  const s = loadState();
  const id = makeId();
  const token = makeId();
  const child = {
    id, token,
    name: profile.name?.trim() || profile.childName?.trim() || "Explorer",
    age: profile.age || 7,
    curriculum: profile.curriculum || "uk-ks2",
    subjects: profile.subjects || ["geography"],
    dailyMinutes: profile.dailyMinutes || 30,
    focus: profile.focus || "",
    createdAt: new Date().toISOString(),
  };
  const plan = generateWeeklyPlan(child, 1);
  const next = {
    ...s,
    children:   { ...s.children,   [id]: child },
    plans:      { ...s.plans,      [id]: plan },
    completion: { ...s.completion, [id]: {} },
    activeId:   id,
  };
  writeRaw(next);
  return { state: next, id };
}

export function updateChild(id, patch) {
  const s = loadState();
  if (!s.children[id]) return s;
  const updated = { ...s.children[id], ...patch };
  const nextPlan = generateWeeklyPlan(updated, (s.plans[id]?.weekNumber) || 1);
  const next = {
    ...s,
    children: { ...s.children, [id]: updated },
    plans:    { ...s.plans,    [id]: nextPlan },
  };
  writeRaw(next);
  return next;
}

export function deleteChild(id) {
  const s = loadState();
  const children  = { ...s.children };  delete children[id];
  const plans     = { ...s.plans };     delete plans[id];
  const completion= { ...s.completion };delete completion[id];
  const activeId  = s.activeId === id ? (Object.keys(children)[0] || null) : s.activeId;
  const next = { children, plans, completion, activeId };
  writeRaw(next);
  return next;
}

export function setActiveChild(id) {
  const s = loadState();
  if (!s.children[id]) return s;
  const next = { ...s, activeId: id };
  writeRaw(next);
  return next;
}

export function regenerateActive() {
  const s = loadState();
  const id = s.activeId;
  if (!id) return s;
  const nextWeek = Math.min(TOTAL_WEEKS, (s.plans[id]?.weekNumber || 0) + 1);
  const plan = generateWeeklyPlan(s.children[id], nextWeek);
  const next = {
    ...s,
    plans:      { ...s.plans,      [id]: plan },
    completion: { ...s.completion, [id]: {} },
  };
  writeRaw(next);
  return next;
}

/**
 * Parent control (STEP 5) — jump the active child (or a specific one) to
 * any of the 36 syllabus weeks. Preserves completion checks in case the
 * parent is peeking ahead and wants to come back.
 */
export function jumpToWeek(id, weekNumber) {
  const s = loadState();
  const targetId = id || s.activeId;
  if (!targetId || !s.children[targetId]) return s;
  const clamped = Math.max(1, Math.min(TOTAL_WEEKS, weekNumber));
  const plan = generateWeeklyPlan(s.children[targetId], clamped);
  const next = {
    ...s,
    plans: { ...s.plans, [targetId]: plan },
  };
  writeRaw(next);
  return next;
}

export function toggleActivityFor(id, day, activityId) {
  const s = loadState();
  if (!s.children[id]) return s;
  const key = `${day}::${activityId}`;
  const cur = s.completion[id] || {};
  const nextChild = { ...cur, [key]: !cur[key] };
  const next = { ...s, completion: { ...s.completion, [id]: nextChild } };
  writeRaw(next);
  return next;
}

/**
 * Cycle 62 · Feb 2026 · Plan-cell status setter.
 * Persists a tri-state (planned/progress/mastered) status against a
 * (week, lesson-code) key so the drawer's "Mark …" buttons produce a
 * durable state that YearMapGrid + TermGridView read on next render.
 * Mirrors the same completion structure used by activity toggles.
 */
export function setPlanCellStatus(id, weekNumber, code, status) {
  if (!id || !code) return null;
  const s = loadState();
  if (!s.children[id]) return s;
  const key = `plan-status::Wk${weekNumber || 0}::${code}`;
  const cur = s.completion[id] || {};
  const next = { ...cur, [key]: status };
  // Prune "planned" (default) so completion doesn't grow unbounded.
  if (!status || status === "planned") delete next[key];
  const nextState = { ...s, completion: { ...s.completion, [id]: next } };
  writeRaw(nextState);
  return nextState;
}


/** LEGACY toggle used by the old (v1) LearningPlan render path. */
export function toggleActivity(state, day, activityId) {
  const key = `${day}::${activityId}`;
  const completion = { ...(state.completion || {}) };
  completion[key] = !completion[key];
  return { ...state, completion };
}

// ── Query helpers ─────────────────────────────────────────────────
export function findChildByToken(token) {
  const s = loadState();
  const child = Object.values(s.children).find((c) => c.token === token);
  if (!child) return null;
  return {
    child,
    plan: s.plans[child.id],
    completion: s.completion[child.id] || {},
  };
}

export function planStatsFor(state, id) {
  const plan = state.plans?.[id];
  const completion = state.completion?.[id] || {};
  if (!plan) return { totalActivities: 0, done: 0, byDay: {} };
  let total = 0; let done = 0; const byDay = {};
  for (const d of plan.days) {
    let dTotal = 0; let dDone = 0;
    for (const a of d.activities) {
      total++; dTotal++;
      if (completion[`${d.day}::${a.id}`]) { done++; dDone++; }
    }
    byDay[d.day] = { total: dTotal, done: dDone };
  }
  return { totalActivities: total, done, byDay };
}

/** Legacy alias used by the old LearningPlan render + TermReport. */
export function planStats(state) {
  if (!state?.plan) return { totalActivities: 0, done: 0, byDay: {} };
  let total = 0; let done = 0; const byDay = {};
  for (const d of state.plan.days) {
    let dTotal = 0; let dDone = 0;
    for (const a of d.activities) {
      total++; dTotal++;
      if (state.completion?.[`${d.day}::${a.id}`]) { done++; dDone++; }
    }
    byDay[d.day] = { total: dTotal, done: dDone };
  }
  return { totalActivities: total, done, byDay };
}

/** Curriculum standards covered by activities marked done. */
export function coveredStandards(state) {
  if (!state?.plan) return [];
  const set = new Set();
  for (const d of state.plan.days) {
    for (const a of d.activities) {
      if (state.completion?.[`${d.day}::${a.id}`]) {
        (a.standards || []).forEach((s) => set.add(s));
      }
    }
  }
  return [...set];
}

/** Build the unique per-student URL for the current origin. Includes an
 *  encoded profile in the hash fragment so the link is portable to another
 *  device (grandma's iPad, a shared tablet, etc). Hash fragments are never
 *  sent to a server — the child's data stays private to the browser. */
export function studentLinkFor(child) {
  if (!child?.token) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const packed = packProfile(child);
  const frag   = packed ? `#p=${packed}` : "";
  return `${origin}/student/${child.token}${frag}`;
}

/** Base64url-encode a minimal profile for cross-device embedding. Only the
 *  fields the StudentPlan page needs — no PII beyond first name and age. */
export function packProfile(child) {
  if (!child) return "";
  try {
    const min = {
      n: child.name,
      a: child.age,
      c: child.curriculum,
      s: child.subjects,
      m: child.dailyMinutes,
      t: child.token,
    };
    const json = JSON.stringify(min);
    // base64url — safe in URL fragments across all browsers.
    if (typeof btoa === "undefined") return "";
    const b64 = btoa(unescape(encodeURIComponent(json)));
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch { return ""; }
}

/** Reverse of packProfile — used by StudentPlan when the token isn't in
 *  localStorage (i.e. the link was opened on a different device). */
export function unpackProfile(fragment) {
  if (!fragment) return null;
  try {
    const s = String(fragment).replace(/-/g, "+").replace(/_/g, "/");
    const pad = s + "===".slice((s.length + 3) % 4);
    if (typeof atob === "undefined") return null;
    const json = decodeURIComponent(escape(atob(pad)));
    const min = JSON.parse(json);
    return {
      id: `remote:${min.t || "unknown"}`,
      token: min.t || "",
      name: min.n || "Explorer",
      age: min.a || 7,
      curriculum: min.c || "uk-ks2",
      subjects: Array.isArray(min.s) ? min.s : ["geography"],
      dailyMinutes: min.m || 30,
      focus: "",
      remote: true, // signals StudentPlan to use in-memory-only completion
    };
  } catch { return null; }
}
